import { useContext, useState, useEffect } from 'react';
import FullRedHeart from '../public/Like_true.svg';
import FullGrayHeart from '../public/icons/fullGrayHeart.svg';
import EditingDocIconBlack from '../public/doing_true.svg';
import EditingDocIconGreen from '../public/doing _false.svg';
// the Eye is the HIDE control. Open eye = the grant is showing;
// closed eye (lid with lashes) = hidden. Viewed has no icon any more?.
import OpenEye from '../public/hide _false.svg';
import ClosedEye from '../public/hide_true.svg';
import { selectedHitContext } from '../GrantView/SelectedHitProvider';
import { useAuth } from '../AuthContext';
import styles from './style.module.css';

// the four marks (Doing / Liked / Viewed / Hidden) live as true/false
// fields ON the grant record itself. Clicking an icon writes the
// field through the backend and the change sticks for everyone? NEED TO CHANGE THIS??
export default function Card({ hit, onMarkChange }) {
  const { selectedHit, setSelectedHit } = useContext(selectedHitContext);
  const { user } = useAuth();

  const [iconsPressed, setIconsPressedState] = useState({
    doing: false,
    liked: false,
    hidden: false,
    viewed: false,
  });

  // The icons start from the grant record's own fields.
  useEffect(() => {
    if (!hit) return;
    setIconsPressedState({
      doing: hit['Doing'] === true,
      liked: hit['Liked'] === true,
      hidden: hit['Hidden'] === true,
      viewed: hit['Viewed'] === true,
    });
  }, [hit]);

  const saveMark = async (name, value) => {
    if (!user) return;
    try {
      // A login pass expires after an hour — ask for a fresh one each time.
      const freshToken = await user.getIdToken();
      const baseurl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${baseurl}/api/setGrantMark`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${freshToken}`,
        },
        body: JSON.stringify({
          grantId: hit['GrantWatch ID#'],
          mark: name,
          value,
        }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      if (onMarkChange) onMarkChange(hit['GrantWatch ID#'], name, value);
      return true;
    } catch (err) {
      console.error('Saving the mark failed:', err);
      return false;
    }
  };

  // Flip on screen right away, then save.
  const toggleIcon = async (name) => {
    const next = !iconsPressed[name];
    setIconsPressedState((prev) => ({ ...prev, [name]: next }));
    const saved = await saveMark(name, next);
    if (!saved) {
      setIconsPressedState((prev) => ({ ...prev, [name]: !next }));
    }
  };

  const selected = shouldHitBeSelected(selectedHit, hit);
  const applicantTypes = joinApplicantTypes(hit && hit['Applicant Types']);
  const agency = hit && hit['Agency'] ? String(hit['Agency']) : '';

  return (
    <div
      className={`${styles.card} ${selected ? styles.cardSelected : ''}`}
      onClick={() => {
        setSelectedHit(hit);
        // Reading a grant records Viewed quietly (for the Viewed button);
        // nothing on the card changes because of it.
        if (!iconsPressed.viewed) {
          setIconsPressedState((prev) => ({ ...prev, viewed: true }));
          saveMark('viewed', true);
        }
      }}
    >
      {/* row 1: the funder */}
      <p className={styles.cardTitle}>{hit['Funding Source']}</p>

      {/* row 2: left and right columns */}
      <div className={styles.cardBody}>
        <div className={styles.cardLeft}>
          <div className={styles.cardEligible}>Eligibility Matched</div>
          <div className={styles.cardAmount}>
            Amount {formatAmount(hit['Amount Min'], hit['Amount Max'])}
          </div>
        </div>
        <div className={styles.cardRight}>
          <div className={styles.cardTypes}>{applicantTypes}</div>
          <div className={styles.cardAgency}>{agency}</div>
        </div>
      </div>

      {/* the marks, top right, in the picture's order: Doing, Liked, Hide */}
      <div className={styles.cardMarks}>
        <img
          className={styles.cardMark}
          alt=""
          title={iconsPressed.doing ? 'Doing' : 'Mark as Doing'}
          src={!iconsPressed.doing ? EditingDocIconGreen : EditingDocIconBlack}
          onClick={(e) => {
            e.stopPropagation();
            toggleIcon('doing');
          }}
        />

        <img
          className={`${styles.cardMark} ${styles.cardMarkHeart}`}
          alt=""
          src={!iconsPressed.liked ? FullGrayHeart : FullRedHeart}
          onClick={(e) => {
            e.stopPropagation(); // Prevent parent click
            toggleIcon('liked'); // Only toggle liked
          }}
        />

        {/* the eye hides and unhides. Open eye while showing; closed
            eye while hidden (in the Hidden view, clicking it brings the grant
            back to the main list). */}
        <img
          className={styles.cardMark}
          alt=""
          src={iconsPressed.hidden ? ClosedEye : OpenEye}
          title={iconsPressed.hidden ? 'Unhide this grant' : 'Hide this grant'}
          onClick={(e) => {
            e.stopPropagation();
            toggleIcon('hidden');
          }}
        />
      </div>
    </div>
  );
}

function shouldHitBeSelected(selectedHit, hit) {
  if (selectedHit && selectedHit.objectID) {
    return selectedHit.objectID === hit.objectID;
  } else if (selectedHit && !selectedHit.objectID) {
    return selectedHit['GrantWatch ID#'] === hit['GrantWatch ID#'];
  }
  return false;
}

// "Applicant Types" may be a list or one string. Joined with " & ";
// blank when there is none.
function joinApplicantTypes(value) {
  if (Array.isArray(value)) {
    return value.map((v) => String(v).trim()).filter(Boolean).join(' & ');
  }
  if (typeof value === 'string') return value.trim();
  return '';
}

// ---------------------------------------------------------------------------
// The same k/m money format as the column-1 Amount pill (Filter/index.js),
// copied here because that file is not to be changed.
// ---------------------------------------------------------------------------

// 1000 -> "$1k", 1500000 -> "$1.5m", 100000000 -> "$100m", 500 -> "$500".
function formatMoney(digits) {
  const n = Number(digits);
  if (!Number.isFinite(n)) return `$${digits}`;
  const short = (value) => {
    const rounded = Math.round(value * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  };
  if (n >= 1000000) return `$${short(n / 1000000)}m`;
  if (n >= 1000) return `$${short(n / 1000)}k`;
  return `$${n}`;
}

// "$100k - $100m", "from $100k", "up to $1m", or "not stated".
function formatAmount(min, max) {
  const hasMin = min !== '' && min != null && Number.isFinite(Number(min));
  const hasMax = max !== '' && max != null && Number.isFinite(Number(max));
  if (hasMin && hasMax) return `${formatMoney(min)} - ${formatMoney(max)}`;
  if (hasMin) return `from ${formatMoney(min)}`;
  if (hasMax) return `up to ${formatMoney(max)}`;
  return 'not stated';
}
